import React from 'react';
import { Cookie, Mail, ArrowLeft, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { usePageMeta } from '../hooks/usePageMeta';

const Section = ({ id, title, children }) => (
  <section id={id} data-testid={`cookie-section-${id}`} className="mb-10">
    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 leading-tight">
      {title}
    </h2>
    <div className="space-y-4 text-gray-700 text-base leading-relaxed">
      {children}
    </div>
  </section>
);

export const CookiePolicy = () => {
  usePageMeta({
    title: 'Cookie Policy | Tower Technology',
    description:
      'Learn how Tower Technology uses technically necessary cookies and browser storage on the towertech-eng.com website.',
    canonical: 'https://towertech-eng.com/cookie-policy',
  });
  return (
    <>
      <Header />
      <main className="pt-32 pb-24 bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50 min-h-screen">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-700 rounded-full mb-6">
                <Cookie className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Cookie Policy
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-800 mb-6"></div>
              <p className="text-base md:text-lg text-gray-600">
                Effective date: September 2, 2026
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-7 md:p-10">
              <p className="text-gray-700 leading-relaxed mb-8">
                This Cookie Policy explains how{' '}
                <span className="font-semibold">
                  Xiamen Tower Technology Co., Ltd.
                </span>{' '}
                ("Tower Technology") uses cookies and similar browser storage
                technologies on the website{' '}
                <a
                  href="https://towertech-eng.com"
                  className="text-blue-700 hover:text-blue-800 underline"
                >
                  towertech-eng.com
                </a>
                .
              </p>

              <Section id="what-are-cookies" title="1. What Are Cookies">
                <p>
                  Cookies are small text files placed on your device when you
                  visit a website. They are commonly used to allow websites to
                  function, remember user preferences and, in some cases,
                  collect information about how a website is used.
                </p>
                <p>
                  This website also uses "browser storage" (such as
                  localStorage), which is a similar technology used to store
                  small pieces of information locally in your browser.
                </p>
              </Section>

              <Section id="necessary" title="2. Necessary Cookies and Storage">
                <p>
                  This website uses only technically necessary cookies and
                  browser storage that are required for the site to function
                  correctly. These are used to:
                </p>
                <ul className="list-disc pl-6 space-y-1.5">
                  <li>Remember whether you have already been shown the cookie information banner during your current browser session</li>
                  <li>Remember your language preference selection when displayed</li>
                </ul>
                <p>
                  These items are stored in your own browser and are not
                  transmitted to third parties. They cannot be disabled
                  without affecting basic site functionality.
                </p>
              </Section>

              <Section id="analytics" title="3. Analytics Cookies">
                <p>
                  This website does not currently use analytics cookies, and
                  we do not track visitor behavior across pages for analytics
                  purposes.
                </p>
                <p>
                  If analytics tools are introduced in the future, this Cookie
                  Policy will be updated accordingly and, where required by
                  applicable law, your consent will be requested through the
                  cookie information banner.
                </p>
              </Section>

              <Section id="advertising" title="4. Advertising and Marketing Cookies">
                <p>
                  This website does not use advertising cookies or marketing
                  trackers. We do not run advertising or retargeting campaigns
                  from this website.
                </p>
              </Section>

              <Section id="third-party" title="5. Third-Party Cookies">
                <p>
                  This website does not intentionally set third-party cookies.
                  Standard third-party services required for the website to
                  function (such as web hosting or email delivery for the
                  project inquiry form) do not place tracking cookies through
                  this website.
                </p>
              </Section>

              <Section id="control" title="6. How to Control or Disable Cookies">
                <p>
                  You can control or delete cookies and browser storage at any
                  time through your browser settings. Please note that
                  disabling necessary cookies or browser storage may affect
                  parts of the website (for example, the cookie information
                  banner may reappear on each visit).
                </p>
                <p>Guidance for common browsers:</p>
                <ul className="list-disc pl-6 space-y-1.5">
                  <li>Chrome: Settings → Privacy and security → Cookies and other site data</li>
                  <li>Firefox: Settings → Privacy &amp; Security → Cookies and Site Data</li>
                  <li>Safari: Preferences → Privacy</li>
                  <li>Edge: Settings → Privacy, search, and services → Cookies</li>
                </ul>
              </Section>

              <Section id="updates" title="7. Updates to This Policy">
                <p>
                  We may update this Cookie Policy from time to time to
                  reflect changes in the technologies used on the website or
                  changes in applicable law. Updated versions will be posted
                  on this page with a revised effective date.
                </p>
              </Section>

              {/* Info Notice */}
              <div className="mt-4 bg-blue-50 border-l-4 border-blue-700 rounded-lg p-5 md:p-6">
                <div className="flex items-start space-x-3">
                  <Info className="w-6 h-6 text-blue-700 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 leading-relaxed">
                    In summary: this website uses only the minimum browser
                    storage required to function. It does not run analytics,
                    advertising or third-party tracking.
                  </p>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-gradient-to-br from-blue-700 to-blue-900 rounded-xl p-6 md:p-8 text-white mt-8">
                <div className="flex items-start space-x-4">
                  <div className="inline-flex items-center justify-center w-11 h-11 bg-white/20 rounded-lg flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl md:text-2xl font-bold mb-2">
                      8. Contact
                    </h2>
                    <p className="text-blue-100 mb-4 leading-relaxed">
                      For any questions about this Cookie Policy, please
                      contact:
                    </p>
                    <a
                      href="mailto:office@towertech-eng.com"
                      className="inline-flex items-center text-white font-semibold hover:text-blue-100 transition-colors break-all"
                    >
                      <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                      office@towertech-eng.com
                    </a>
                    <div className="mt-4 text-sm text-blue-100 leading-relaxed">
                      <div className="font-semibold text-white">
                        Xiamen Tower Technology Co., Ltd.
                      </div>
                      <div>Xiamen (Fujian), China</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <Link
                to="/"
                data-testid="cookie-back-home"
                className="inline-flex items-center px-6 py-3 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};
